import { useCachedMembers } from '@easy-expense/data-firestore-client';
import { PaymentMethod } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getUserDisplayName } from '@easy-expense/utils-shared';
import {
  MRT_GlobalFilterTextField,
  MRT_Row,
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React from 'react';

import { UpsertPaymentMethod } from './UpsertPaymentMethod.component';
import { Modal } from '../Shared/Modal.component';
import { TableTextSize } from '../Shared/TableTextSize';

export const PaymentMethodsTable: React.FC<React.PropsWithChildren<object>> = () => {
  const paymentMethods = Data.paymentMethods.use();
  const members = useCachedMembers();
  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] = React.useState(false);
  const [selectedPaymentMethodKey, setSelectedPaymentMethodKey] = React.useState<
    string | undefined
  >(undefined);

  const columns = React.useMemo<MRT_ColumnDef<PaymentMethod>[]>(
    () => [
      {
        id: 'key',
        header: '',
        maxSize: 10,
        Cell: ({ row }) => {
          return (
            <Layout.PressableRow
              border={[1, 'solid', 'destructiveDark']}
              align
              center
              style={{ backgroundColor: theme.colors.destructiveXLight, width: 32, height: 32 }}
              radius={100}
              onClick={(e: any) => {
                e.stopPropagation();
                if (!row.original.key) {
                  return;
                }
                Data.paymentMethods.archive(row.original.key);
              }}
            >
              <Icon name="trash-outline" size={16} color={theme.colors.destructiveDark} />
            </Layout.PressableRow>
          );
        },
      },
      {
        accessorKey: 'value.name',
        header: getTranslation('Name'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>{getTranslation('Name')}</OpenSans.Secondary>
        ),
        size: 200,
        Cell: ({ row }) => {
          return (
            <OpenSans.Primary size={TableTextSize.m} weight="bold-700">
              {row.original.value.name}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: 'value.institution',
        header: getTranslation('Institution'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>
            {getTranslation('Institution')}
          </OpenSans.Secondary>
        ),
        size: 200,
        Cell: ({ row }) => {
          return (
            <OpenSans.Primary size={TableTextSize.m} weight="bold-700">
              {row.original.value.institution}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: 'value.digits',
        header: getTranslation('Last 4 Digits'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>
            {getTranslation('Last 4 Digits')}
          </OpenSans.Secondary>
        ),
        size: 150,
        Cell: ({ row }) => {
          return (
            <OpenSans.Primary size={TableTextSize.m} weight="bold-700">
              {row.original.value.digits || '-'}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: 'createdBy',
        header: getTranslation('Assign to'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>
            {getTranslation('Assign to')}
          </OpenSans.Secondary>
        ),
        size: 200,
        Cell: ({ row }) => {
          const userID = row.original.createdBy;
          const member = members.find((m) => m.userID === userID);
          const memberName = member ? getUserDisplayName(member, '') : '-';
          return (
            <OpenSans.Primary size={TableTextSize.m} weight="bold-700">
              {memberName}
            </OpenSans.Primary>
          );
        },
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: paymentMethods,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
      sorting: [{ desc: false, id: 'value.name' }],
    },
    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
    },
    autoResetPageIndex: false,
    autoResetExpanded: false,
    enableColumnActions: false,
    columnFilterDisplayMode: 'subheader',
    getRowId: (row) => row.key ?? '',
    enableDensityToggle: false,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: 'pointer',
      },
      onClick: () => displayPaymentMethodEditModal(row),
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
    renderTopToolbar: ({ table }) => {
      return (
        <Layout.Row py>
          <MRT_GlobalFilterTextField
            table={table}
            sx={{
              borderColor: theme.colors.inputBorder,
              '& .MuiInputBase-root': {
                backgroundColor: theme.colors.inputBackground,
                borderRadius: 2,
                borderColor: theme.colors.success,
                width: 500,
                paddingRight: 1,
              },
            }}
          />

          <Spacer.Horizontal size={16} />

          <Layout.PressableRow
            onClick={() => {
              setSelectedPaymentMethodKey('');
              setShowAddPaymentMethodModal(true);
            }}
            bg="brandPrimaryXLight"
            border={[1, 'solid', 'brandPrimary']}
            py={4}
            px
            radius={100}
            align
          >
            <Icon name="add-outline" size={16} color={theme.colors.brandPrimary} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Pressable>{getTranslation('Add Payment Method')}</OpenSans.Pressable>
          </Layout.PressableRow>
        </Layout.Row>
      );
    },
  });

  function displayPaymentMethodEditModal(row: MRT_Row<PaymentMethod>) {
    setSelectedPaymentMethodKey(row.original.key);
    setShowAddPaymentMethodModal(true);
  }

  return (
    <Layout.Column>
      <Spacer.Vertical size={24} />

      <Spacer.Vertical
        size={12}
        style={{
          borderTop: `1px solid ${theme.colors.inputBorder}`,
        }}
      />

      <MaterialReactTable table={table} />

      <Modal showModal={showAddPaymentMethodModal} setShowModal={setShowAddPaymentMethodModal}>
        <UpsertPaymentMethod
          onDone={() => setShowAddPaymentMethodModal(false)}
          onDiscard={() => setShowAddPaymentMethodModal(false)}
          selectedPaymentMethodKey={selectedPaymentMethodKey}
        />
      </Modal>
    </Layout.Column>
  );
};
